import { useEffect, useRef, useState } from 'react';
import Layout from '../../components/Layout';
import { api, parsePaginatedResponse } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { useEntitlements } from '../../hooks/useEntitlements';
import { Link } from 'react-router-dom';

interface GameType {
    id: string;
    code: string;
    name: string;
    group_code: string;
    description?: string;
}

interface Template {
    id: string;
    name: string;
    type: string;
    code: string;
    version?: string;
    description?: string;
    status?: string;
    runtime_url?: string;
    sdk_version?: string;
    game_type?: GameType;
    game_type_id?: string;
    config_schema?: Record<string, any>;
    asset_schema?: Record<string, any>;
    created_at?: string;
}

const gameTypeIcons: Record<string, string> = {
    'lucky_wheel': '🎡',
    'flip_card': '🃏',
    'scratch_card': '🎫',
    'egg_pick': '🥚',
    'catch_item': '🎯',
    'random_code': '🎟️',
    'lucky-wheel': '🎡',
    'flip-card': '🃏',
    'scratch-card': '🎫',
    'egg-pick': '🥚',
    'catch-item': '🎯',
    'random-code': '🎟️',
    'default': '🎮',
};

const statusColors: Record<string, string> = {
    approved: 'bg-green-100 text-green-800',
    review: 'bg-yellow-100 text-yellow-800',
    draft: 'bg-gray-100 text-gray-800',
    deprecated: 'bg-red-100 text-red-800',
};

function getGameTypeCode(template: Template): string {
    return template.code?.replace(/_v\d+$/, '') || template.game_type?.code || template.type || '';
}

function getTemplateName(template: Template): string {
    return template.name || template.game_type?.name || template.code || 'Mẫu chưa đặt tên';
}

function getTemplateDescription(template: Template): string {
    return template.description || template.game_type?.description || '';
}

export default function GameTemplates() {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [gameTypes, setGameTypes] = useState<GameType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creating, setCreating] = useState(false);
    // Upload ZIP state
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');
    const fileRef = useRef<HTMLInputElement>(null);
    // Preview state
    const [previewId, setPreviewId] = useState<string | null>(null);
    const [newTemplate, setNewTemplate] = useState({
        name: '',
        code: '',
        game_type_id: '',
        version: '1.0.0',
        runtime_url: '',
        sdk_version: '1.0.0',
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
    const [editTemplate, setEditTemplate] = useState({
        name: '',
        code: '',
        game_type_id: '',
        version: '1.0.0',
        runtime_url: '',
        sdk_version: '1.0.0',
        status: 'draft',
        config_schema: '{}',
        asset_schema: '{}',
    });
    const [configJsonError, setConfigJsonError] = useState('');
    const [assetJsonError, setAssetJsonError] = useState('');

    const validateConfigJson = (value: string) => {
        if (!value.trim()) {
            setConfigJsonError('');
            return;
        }
        try {
            JSON.parse(value);
            setConfigJsonError('');
        } catch (e: any) {
            setConfigJsonError(e.message);
        }
    };

    const validateAssetJson = (value: string) => {
        if (!value.trim()) {
            setAssetJsonError('');
            return;
        }
        try {
            JSON.parse(value);
            setAssetJsonError('');
        } catch (e: any) {
            setAssetJsonError(e.message);
        }
    };
    
    const { user } = useAuthStore();
    const { hasGameType, entitlements } = useEntitlements();
    const isSuperAdmin = user?.roles?.includes('super_admin');

    const fetchTemplates = async () => {
        try {
            setError('');
            const data = await api.getGameTemplates(1, 50);
            const { items } = parsePaginatedResponse<Template>(data);
            setTemplates(items);
        } catch (err: any) {
            console.error('Failed to fetch templates:', err);
            setError(err.message || 'Failed to load templates');
        } finally {
            setLoading(false);
        }
    };

    const fetchGameTypes = async () => {
        try {
            const data = await api.getGameTypes();
            setGameTypes(Array.isArray(data) ? data : []);
        } catch (err) {
            console.warn('Failed to fetch game types:', err);
        }
    };

    useEffect(() => {
        fetchTemplates();
        fetchGameTypes();
    }, []);

    const handleDelete = async (id: string, name: string) => {
        if (!confirm(`Bạn có chắc muốn xóa "${name}"?`)) return;
        try {
            await api.deleteGameTemplate(id);
            setTemplates(prev => prev.filter(t => t.id !== id));
        } catch (err: any) {
            console.error('Failed to delete template:', err);
            alert(err.message || 'Failed to delete template');
        }
    };

    const handleCreate = async () => {
        if (!newTemplate.name || !newTemplate.code || !newTemplate.game_type_id) return;
        setCreating(true);
        try {
            await api.createGameTemplate({
                ...newTemplate,
                status: 'draft',
                config_schema: {},
                asset_schema: {},
                created_by: user?.id || null,
            });
            setShowCreateModal(false);
            setNewTemplate({ name: '', code: '', game_type_id: '', version: '1.0.0', runtime_url: '', sdk_version: '1.0.0' });
            await fetchTemplates();
        } catch (err: any) {
            console.error('Failed to create template:', err);
            alert(err.message || 'Failed to create template');
        } finally {
            setCreating(false);
        }
    };

    const handleOpenEditModal = (template: Template) => {
        setEditingTemplateId(template.id);
        setEditTemplate({
            name: template.name || '',
            code: template.code || '',
            game_type_id: template.game_type_id || '',
            version: template.version || '1.0.0',
            runtime_url: template.runtime_url || '',
            sdk_version: template.sdk_version || '1.0.0',
            status: template.status || 'draft',
            config_schema: JSON.stringify(template.config_schema || {}, null, 2),
            asset_schema: JSON.stringify(template.asset_schema || {}, null, 2),
        });
        setConfigJsonError('');
        setAssetJsonError('');
        setShowEditModal(true);
    };

    const handleUpdate = async () => {
        if (!editingTemplateId || !editTemplate.name || !editTemplate.code || !editTemplate.game_type_id) return;
        
        let parsedConfig = {};
        let parsedAsset = {};
        try {
            parsedConfig = JSON.parse(editTemplate.config_schema || '{}');
        } catch (e) {
            alert('JSON Config Schema không hợp lệ!');
            return;
        }
        try {
            parsedAsset = JSON.parse(editTemplate.asset_schema || '{}');
        } catch (e) {
            alert('JSON Asset Schema không hợp lệ!');
            return;
        }

        setUpdating(true);
        try {
            await api.updateGameTemplate(editingTemplateId, {
                ...editTemplate,
                config_schema: parsedConfig,
                asset_schema: parsedAsset,
            });
            setShowEditModal(false);
            setEditingTemplateId(null);
            await fetchTemplates();
        } catch (err: any) {
            console.error('Failed to update template:', err);
            alert(err.message || 'Failed to update template');
        } finally {
            setUpdating(false);
        }
    };

    const autoGenerateCode = (name: string) => {
        return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') + '_v1';
    };

    const handleUploadZip = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) { setUploadError('Vui lòng chọn file .zip trước'); return; }
        setUploading(true);
        setUploadError('');
        setUploadSuccess('');
        try {
            await api.uploadGameTemplateZip(file);
            setShowUploadModal(false);
            setUploadSuccess('Tải lên mẫu thành công!');
            if (fileRef.current) fileRef.current.value = '';
            await fetchTemplates();
            setTimeout(() => setUploadSuccess(''), 4000);
        } catch (err: any) {
            setUploadError(err.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64"><div className="text-gray-500 font-semibold">Đang tải...</div></div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Kho trò chơi</h1>
                    <p className="text-gray-500 mt-1.5 font-medium">Duyệt danh sách game marketing có sẵn trong hệ thống</p>
                </div>
                {isSuperAdmin && (
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => { setShowUploadModal(true); setUploadError(''); setUploadSuccess(''); }}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 font-medium text-sm"
                        >
                            <span>📦</span> Tải lên ZIP
                        </button>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium text-sm"
                        >
                            <span>➕</span> Mẫu mới
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 font-medium">
                    {error}
                </div>
            )}

            {uploadSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2 font-medium">
                    <span>✅</span> {uploadSuccess}
                </div>
            )}

            {templates.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <div className="text-6xl mb-4">📦</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Chưa có mẫu nào</h3>
                    <p className="text-gray-500 mb-4 font-medium">Tạo mẫu trò chơi đầu tiên để bắt đầu.</p>
                    {isSuperAdmin && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-semibold"
                        >
                            Tạo mẫu
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map((template) => {
                        const typeCode = getGameTypeCode(template);
                        const isAllowed = hasGameType(typeCode);
                        const icon = gameTypeIcons[typeCode] || gameTypeIcons[typeCode?.replace(/_/g, '-')] || gameTypeIcons.default;
                        
                        return (
                            <div 
                                key={template.id} 
                                className={`bg-white p-6 rounded-2xl shadow-sm border border-gray-150 transition-all duration-300 relative overflow-hidden flex flex-col justify-between ${
                                    isAllowed 
                                    ? 'hover:shadow-lg hover:-translate-y-0.5' 
                                    : 'bg-gray-50/70 border-gray-200/60 opacity-80'
                                }`}
                            >
                                {/* Lock Badge overlay for premium games */}
                                {!isAllowed && (
                                    <div className="absolute top-3 right-3 bg-red-100 text-red-700 text-[10px] font-bold uppercase px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm">
                                        <span>🔒</span> Yêu cầu gói Sự kiện+
                                    </div>
                                )}

                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="text-4xl filter drop-shadow-sm">{icon}</div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded font-bold font-mono">v{template.version || '1.0'}</span>
                                            {isSuperAdmin && template.status && (
                                                <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${statusColors[template.status] || 'bg-gray-100 text-gray-800'}`}>
                                                    {template.status}
                                                </span>
                                            )}
                                            {isSuperAdmin && (
                                                <div className="flex items-center gap-2 ml-1">
                                                    <button
                                                        onClick={() => handleOpenEditModal(template)}
                                                        className="text-blue-500 hover:text-blue-700 text-xs font-bold"
                                                        title="Sửa mẫu"
                                                    >
                                                        Sửa
                                                    </button>
                                                    <span className="text-gray-300">|</span>
                                                    <button
                                                        onClick={() => handleDelete(template.id, getTemplateName(template))}
                                                        className="text-red-400 hover:text-red-600 text-xs font-bold"
                                                        title="Xóa mẫu"
                                                    >
                                                        Xóa
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <h3 className="font-bold text-gray-900 text-lg leading-tight">{getTemplateName(template)}</h3>
                                    <p className="text-sm text-gray-500 mt-2 line-clamp-2 leading-relaxed">
                                        {getTemplateDescription(template) || 'Chưa có mô tả chi tiết cho trò chơi này.'}
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <span className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-semibold flex items-center gap-1 border border-slate-200" title="Số lượng tham số logic trong config_schema">
                                            ⚙️ {Object.keys(template.config_schema || {}).length} tham số
                                        </span>
                                        <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-semibold flex items-center gap-1 border border-amber-200" title="Số lượng tài nguyên hình ảnh trong asset_schema">
                                            🖼️ {Object.keys(template.asset_schema || {}).length} tài nguyên
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-6 pt-4 border-t border-gray-100">
                                    {isAllowed ? (
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[10px] text-blue-600 bg-blue-50/70 px-2 py-0.5 rounded-full font-bold font-mono uppercase">
                                                    {typeCode || template.code}
                                                </span>
                                                <button
                                                    onClick={() => window.open(`/preview/template/${template.id}`, '_blank')}
                                                    className="text-xs text-blue-600 hover:text-blue-800 font-bold ml-1.5 transition-colors"
                                                >
                                                    Chạy thử →
                                                </button>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    const url = `${window.location.origin}/preview/template/${template.id}`;
                                                    navigator.clipboard.writeText(url);
                                                    setUploadSuccess('URL preview đã sao chép!');
                                                    setTimeout(() => setUploadSuccess(''), 3000);
                                                }}
                                                className="text-xs text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-200/80 px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1 font-semibold border border-gray-200"
                                                title="Copy Game URL"
                                            >
                                                🔗 Sao chép URL
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-2.5">
                                            <div className="flex items-center justify-between border-b border-dashed border-gray-150 pb-2">
                                                <span className="text-[10px] text-red-500 font-bold bg-red-50 px-2 py-0.5 rounded">Chưa mở khóa</span>
                                                <button
                                                    onClick={() => window.open(`/preview/template/${template.id}`, '_blank')}
                                                    className="text-xs text-indigo-600 hover:text-indigo-800 font-extrabold flex items-center gap-1 transition-all cursor-pointer"
                                                >
                                                    🎮 Chạy thử Demo →
                                                </button>
                                            </div>
                                            <Link
                                                to="/billing"
                                                className="w-full text-center text-xs font-extrabold py-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl shadow-sm hover:shadow-md transition-all uppercase tracking-wider block"
                                            >
                                                👑 Nâng cấp để Dùng chiến dịch
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Upload ZIP Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">Tải lên mẫu trò chơi</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            Upload a <code className="bg-gray-100 px-1 rounded">.zip</code> file containing{' '}
                            <code className="bg-gray-100 px-1 rounded">template.json</code> and game assets.
                        </p>

                        <label className="block w-full border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors">
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".zip"
                                className="hidden"
                                onChange={() => { setUploadError(''); }}
                            />
                            <div className="text-4xl mb-2">📦</div>
                            <p className="text-sm text-gray-600">Nhấp để chọn hoặc kéo thả file ZIP</p>
                            <p className="text-xs text-gray-400 mt-1">Tối đa 50 MB</p>
                        </label>

                        {uploadError && (
                            <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
                                ⚠️ {uploadError}
                            </div>
                        )}

                        <div className="mt-3 bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
                            <strong className="text-gray-700">Định dạng template.json:</strong>
                            <pre className="mt-1 text-gray-600 overflow-x-auto">{`{
  "name": "Lucky Wheel v2",
  "code": "lucky_wheel_v2",
  "gameType": "lucky_wheel",
  "version": "1.0.0"
}`}</pre>
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => { setShowUploadModal(false); setUploadError(''); }}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleUploadZip}
                                disabled={uploading}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {uploading ? (
                                    <><span className="animate-spin">⏳</span> Đang tải lên...</>
                                ) : (
                                    <><span>📤</span> Tải lên</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {previewId && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setPreviewId(null); }}
                >
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                            <div>
                                <h3 className="font-semibold text-gray-900">Xem trước trò chơi</h3>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    {templates.find(t => t.id === previewId)
                                        ? getTemplateName(templates.find(t => t.id === previewId)!)
                                        : previewId}
                                </p>
                            </div>
                            <button
                                onClick={() => setPreviewId(null)}
                                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                                title="Close preview"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="flex-1 overflow-hidden rounded-b-xl" style={{ minHeight: '480px' }}>
                            <iframe
                                src={api.getGameTemplatePreviewUrl(previewId)}
                                className="w-full h-full border-0"
                                style={{ minHeight: '480px' }}
                                title="Game preview"
                                sandbox="allow-scripts"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Create Template Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Mẫu trò chơi mới</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Loại trò chơi</label>
                                <select
                                    value={newTemplate.game_type_id}
                                    onChange={(e) => setNewTemplate(prev => ({ ...prev, game_type_id: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Chọn loại trò chơi...</option>
                                    {gameTypes.map((gt) => (
                                        <option key={gt.id} value={gt.id}>
                                            {gameTypeIcons[gt.code] || '🎮'} {gt.name} ({gt.code})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tên mẫu</label>
                                    <input
                                        type="text"
                                        value={newTemplate.name}
                                        onChange={(e) => setNewTemplate(prev => ({
                                            ...prev,
                                            name: e.target.value,
                                            code: prev.code === autoGenerateCode(prev.name) || !prev.code
                                                ? autoGenerateCode(e.target.value) : prev.code,
                                        }))}
                                        placeholder="Lucky Wheel v2"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Mã</label>
                                    <input
                                        type="text"
                                        value={newTemplate.code}
                                        onChange={(e) => setNewTemplate(prev => ({ ...prev, code: e.target.value }))}
                                        placeholder="lucky_wheel_v2"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phiên bản</label>
                                    <input
                                        type="text"
                                        value={newTemplate.version}
                                        onChange={(e) => setNewTemplate(prev => ({ ...prev, version: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phiên bản SDK</label>
                                    <input
                                        type="text"
                                        value={newTemplate.sdk_version}
                                        onChange={(e) => setNewTemplate(prev => ({ ...prev, sdk_version: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Runtime URL</label>
                                <input
                                    type="text"
                                    value={newTemplate.runtime_url}
                                    onChange={(e) => setNewTemplate(prev => ({ ...prev, runtime_url: e.target.value }))}
                                    placeholder="https://games.example.com/lucky_wheel_v2/index.html"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleCreate}
                                disabled={!newTemplate.name || !newTemplate.code || !newTemplate.game_type_id || creating}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {creating ? 'Đang tạo...' : 'Tạo mẫu'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Template Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-2xl flex flex-col" style={{ maxHeight: '95vh' }}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex-shrink-0">Cập nhật mẫu trò chơi</h3>
                        <div className="space-y-4 overflow-y-auto pr-1 flex-1 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Loại trò chơi</label>
                                <select
                                    value={editTemplate.game_type_id}
                                    onChange={(e) => setEditTemplate(prev => ({ ...prev, game_type_id: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Chọn loại trò chơi...</option>
                                    {gameTypes.map((gt) => (
                                        <option key={gt.id} value={gt.id}>
                                            {gameTypeIcons[gt.code] || '🎮'} {gt.name} ({gt.code})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tên mẫu</label>
                                    <input
                                        type="text"
                                        value={editTemplate.name}
                                        onChange={(e) => setEditTemplate(prev => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))}
                                        placeholder="Lucky Wheel v2"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Mã</label>
                                    <input
                                        type="text"
                                        value={editTemplate.code}
                                        onChange={(e) => setEditTemplate(prev => ({ ...prev, code: e.target.value }))}
                                        placeholder="lucky_wheel_v2"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phiên bản</label>
                                    <input
                                        type="text"
                                        value={editTemplate.version}
                                        onChange={(e) => setEditTemplate(prev => ({ ...prev, version: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phiên bản SDK</label>
                                    <input
                                        type="text"
                                        value={editTemplate.sdk_version}
                                        onChange={(e) => setEditTemplate(prev => ({ ...prev, sdk_version: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Trạng thái</label>
                                    <select
                                        value={editTemplate.status}
                                        onChange={(e) => setEditTemplate(prev => ({ ...prev, status: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="draft">Draft (Nháp)</option>
                                        <option value="active">Active (Hoạt động)</option>
                                        <option value="deprecated">Deprecated (Ngừng hỗ trợ)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Runtime URL</label>
                                    <input
                                        type="text"
                                        value={editTemplate.runtime_url}
                                        onChange={(e) => setEditTemplate(prev => ({ ...prev, runtime_url: e.target.value }))}
                                        placeholder="https://games.example.com/lucky_wheel_v2/index.html"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                    />
                                </div>
                            </div>

                            {/* Schema editors */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex justify-between items-center">
                                        <span>Cấu hình Schema (JSON)</span>
                                        {configJsonError && <span className="text-[10px] text-red-500 font-medium">Lỗi cú pháp</span>}
                                    </label>
                                    <textarea
                                        value={editTemplate.config_schema}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setEditTemplate(prev => ({ ...prev, config_schema: val }));
                                            validateConfigJson(val);
                                        }}
                                        rows={8}
                                        className={`w-full px-3 py-2 border rounded-lg font-mono text-xs focus:ring-2 focus:ring-blue-500 ${configJsonError ? 'border-red-300 bg-red-50/30' : 'border-gray-300'}`}
                                        placeholder="{}"
                                    />
                                    {configJsonError && (
                                        <p className="text-[10px] text-red-600 mt-1 font-medium bg-red-50/70 p-1.5 rounded border border-red-100 whitespace-pre-wrap">{configJsonError}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex justify-between items-center">
                                        <span>Tài nguyên Schema (JSON)</span>
                                        {assetJsonError && <span className="text-[10px] text-red-500 font-medium">Lỗi cú pháp</span>}
                                    </label>
                                    <textarea
                                        value={editTemplate.asset_schema}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setEditTemplate(prev => ({ ...prev, asset_schema: val }));
                                            validateAssetJson(val);
                                        }}
                                        rows={8}
                                        className={`w-full px-3 py-2 border rounded-lg font-mono text-xs focus:ring-2 focus:ring-blue-500 ${assetJsonError ? 'border-red-300 bg-red-50/30' : 'border-gray-300'}`}
                                        placeholder="{}"
                                    />
                                    {assetJsonError && (
                                        <p className="text-[10px] text-red-600 mt-1 font-medium bg-red-50/70 p-1.5 rounded border border-red-100 whitespace-pre-wrap">{assetJsonError}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 flex-shrink-0 pt-2 border-t border-gray-100">
                            <button
                                onClick={() => { setShowEditModal(false); setEditingTemplateId(null); }}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleUpdate}
                                disabled={!editTemplate.name || !editTemplate.code || !editTemplate.game_type_id || !!configJsonError || !!assetJsonError || updating}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
                            >
                                {updating ? 'Đang cập nhật...' : 'Lưu thay đổi'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
